import React from "react";
import { useTranslate } from "@refinedev/core";
import { Box, Typography } from "@mui/material";
import { Eye, EyeOff } from "lucide-react";
import { EVENT_COLORS } from "../../../constants/eventColors";

export interface MapLayerVisibility {
  alerts: boolean;
  journeys: boolean;
  forbiddenZones: boolean;
}

interface MapLayerTogglesProps {
  visibility: MapLayerVisibility;
  onChange: (layer: keyof MapLayerVisibility) => void;
  alertCount: number;
  journeyCount: number;
  forbiddenZoneCount: number;
}

const INACTIVE_COLOR = "#9e9e9e";

const layers: {
  key: keyof MapLayerVisibility;
  labelKey: string;
  fallback: string;
  colorKey: "alert" | "journey" | "forbidden_zone";
}[] = [
  { key: "alerts", labelKey: "monitoring.alerts", fallback: "Alertes", colorKey: "alert" },
  { key: "journeys", labelKey: "monitoring.journeys", fallback: "Trajets", colorKey: "journey" },
  { key: "forbiddenZones", labelKey: "monitoring.forbiddenZones", fallback: "Zones interdites", colorKey: "forbidden_zone" },
];

function LayerPill({
  label,
  count,
  color,
  active,
  onClick,
}: {
  label: string;
  count: number;
  color: string;
  active: boolean;
  onClick: () => void;
}) {
  const bgColor = active ? color : INACTIVE_COLOR;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        backgroundColor: bgColor,
        borderRadius: "20px",
        px: 0.5,
        py: 0.4,
        cursor: "pointer",
        transition: "all 0.2s ease",
        userSelect: "none",
        "&:hover": {
          opacity: 0.85,
        },
      }}
    >
      {/* Count circle */}
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: "0.65rem",
            fontWeight: 700,
            color: bgColor,
            lineHeight: 1,
          }}
        >
          {count}
        </Typography>
      </Box>

      {/* Label */}
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 600,
          color: "#fff",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>

      {/* Eye icon circle */}
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          backgroundColor: "rgba(255,255,255,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {active ? (
          <Eye size={13} color="#fff" strokeWidth={2.5} />
        ) : (
          <EyeOff size={13} color="#fff" strokeWidth={2.5} />
        )}
      </Box>
    </Box>
  );
}

export const MapLayerToggles: React.FC<MapLayerTogglesProps> = ({
  visibility,
  onChange,
  alertCount,
  journeyCount,
  forbiddenZoneCount,
}) => {
  const translate = useTranslate();

  const counts: Record<keyof MapLayerVisibility, number> = {
    alerts: alertCount,
    journeys: journeyCount,
    forbiddenZones: forbiddenZoneCount,
  };

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
      <Typography variant="caption" sx={{ fontWeight: 500, color: "text.secondary", whiteSpace: "nowrap" }}>
        {translate("monitoring.showEventTypes", "Afficher les évènements de type :")}
      </Typography>
      {layers.map(({ key, labelKey, fallback, colorKey }) => (
        <LayerPill
          key={key}
          label={translate(labelKey, fallback)}
          count={counts[key]}
          color={EVENT_COLORS[colorKey].main}
          active={visibility[key]}
          onClick={() => onChange(key)}
        />
      ))}
    </Box>
  );
};
